import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, Wallet, Loader2, RefreshCw, AlertTriangle, Cake,
  Users as UsersIcon, Calendar, ClockAlert, TrendingUp,
} from "lucide-react";

/**
 * KeyPay cross-client payroll rollup. Mirrors the Xero rollup pattern:
 * reads `keypay_client_snapshots` via `GET /api/integrations/keypay/rollup`,
 * with a manual sync button (`POST /rollup/refresh`) and a nightly
 * APScheduler job (`keypay_rollup_sync` @ 07:00 Sydney).
 */
export default function KeyPayRollupPage() {
  const [data, setData] = useState(null);
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, e] = await Promise.all([
        api.get("/integrations/keypay/rollup"),
        api.get("/integrations/keypay/upcoming-events?days=30"),
      ]);
      setData(r.data);
      setEvents(e.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load KeyPay rollup");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data: result } = await api.post(
        "/integrations/keypay/rollup/refresh", {}, { timeout: 180000 });
      toast.success(`Synced ${result.synced ?? 0} of ${result.clients ?? 0} clients`);
      if (result.errors?.length) {
        toast.error(`${result.errors.length} client(s) had errors`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally { setRefreshing(false); }
  };

  const totals = data?.totals || {};
  const clients = data?.clients || [];
  const missing = data?.missing || [];

  return (
    <div className="p-6 max-w-7xl" data-testid="keypay-rollup-page">
      <Link to="/integrations/keypay"
        className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mb-3"
        data-testid="keypay-rollup-back">
        <ArrowLeft className="h-3 w-3" /> Back to KeyPay
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-[#FF6B35]/10">
          <Wallet className="h-6 w-6 text-[#FF6B35]" />
        </div>
        <div className="flex-1">
          <div className="label-overline">KEYPAY · CROSS-CLIENT ROLLUP</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">
            All payroll clients
          </h1>
          {data?.last_synced_at && (
            <div className="text-[11px] text-zinc-500 mt-0.5 font-mono">
              Last synced: {new Date(data.last_synced_at).toLocaleString()}
              <span className="ml-2 text-zinc-400">
                · cached snapshot · nightly auto-refresh @ 07:00 Sydney
              </span>
            </div>
          )}
        </div>
        <Button size="sm" variant="outline" className="rounded-sm"
          onClick={refresh} disabled={refreshing}
          data-testid="keypay-rollup-refresh">
          {refreshing
            ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          {refreshing ? "Syncing…" : "Sync now"}
        </Button>
      </div>

      {loading ? (
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-12 text-center">
            <Loader2 className="h-6 w-6 animate-spin inline" />
          </CardContent>
        </Card>
      ) : (data?.client_count ?? 0) === 0 ? (
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-8 text-center text-sm italic text-zinc-500">
            No clients have KeyPay configured yet. Add one from the
            <Link to="/integrations/keypay" className="text-[#FF6B35] font-semibold ml-1">
              KeyPay setup page
            </Link>.
          </CardContent>
        </Card>
      ) : clients.length === 0 ? (
        <Card className="rounded-sm border-amber-200 bg-amber-50">
          <CardContent className="p-6 text-sm text-amber-900">
            <div className="font-display font-bold mb-1">Cache is empty.</div>
            <div className="text-xs">
              {missing.length} client(s) configured but never synced. Click
              <span className="font-mono mx-1">Sync now</span>
              to run the first rollup.
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Grand totals */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4"
            data-testid="keypay-rollup-totals">
            <KpiTile label="Active employees" value={totals.active_employee_count ?? 0}
              icon={UsersIcon} accent="text-emerald-700" />
            <KpiTile label="Payroll (30d)" value={fmtMoney(totals.last_30d_spend)}
              icon={TrendingUp} accent="text-emerald-700" />
            <KpiTile label="Pending leave" value={totals.pending_leave_count ?? 0}
              icon={Calendar}
              accent={(totals.pending_leave_count ?? 0) > 0 ? "text-amber-700" : ""} />
            <KpiTile label="Pending timesheets" value={totals.pending_timesheet_count ?? 0}
              icon={ClockAlert}
              accent={(totals.pending_timesheet_count ?? 0) > 0 ? "text-amber-700" : ""} />
            <KpiTile label={`Upcoming (30d)`}
              value={`${events?.birthdays ?? 0} 🎂 · ${events?.anniversaries ?? 0} 🎉`}
              icon={Cake} accent="text-purple-700" />
          </div>

          {missing.length > 0 && (
            <Card className="rounded-sm border-amber-200 bg-amber-50 mb-4">
              <CardContent className="p-3 text-xs text-amber-900 flex items-start gap-2">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-semibold">{missing.length}</span> client(s)
                  configured but missing a cached snapshot:{" "}
                  {missing.map((m) => m.client_name).join(", ")}. Click Sync now.
                </div>
              </CardContent>
            </Card>
          )}

          {/* Birthdays / Anniversaries widget (cross-client) */}
          {(events?.events || []).length > 0 && (
            <Card className="rounded-sm border-zinc-200 mb-4" data-testid="keypay-rollup-events">
              <CardContent className="p-0">
                <div className="px-4 py-2 border-b border-zinc-200 bg-zinc-50 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600 flex items-center gap-1.5">
                  <Cake className="h-3 w-3" /> Birthdays &amp; anniversaries · next 30 days · cross-client
                </div>
                <div className="divide-y divide-zinc-100">
                  {(events.events || []).slice(0, 12).map((e, i) => (
                    <div key={`${e.client_id}-${e.employee_id}-${e.kind}-${i}`}
                      className="px-4 py-2 grid grid-cols-[110px_1fr_140px_1fr_80px] gap-3 items-center text-sm">
                      <span className="font-mono text-xs text-zinc-600">{e.date}</span>
                      <Badge variant="outline" className={`rounded-sm text-[10px] ${
                        e.kind === "birthday"
                          ? "bg-pink-50 text-pink-700 border-pink-200"
                          : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                        {e.kind === "birthday" ? "🎂 Birthday" : "🎉 Anniversary"}
                        {e.years ? ` · ${e.years}y` : ""}
                      </Badge>
                      <Link to={`/integrations/keypay/clients/${e.client_id}/employees/${e.employee_id}`}
                        className="truncate font-semibold hover:text-[#FF6B35]">
                        {e.employee_name}
                      </Link>
                      <Link to={`/integrations/keypay/clients/${e.client_id}`}
                        className="truncate text-xs text-zinc-500 hover:text-zinc-900">
                        {e.client_name}
                      </Link>
                      <span className="text-xs text-zinc-500 text-right font-mono">
                        {e.is_today ? "today" : `in ${e.days_away}d`}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Per-client table */}
          <Card className="rounded-sm border-zinc-200">
            <CardContent className="p-0">
              <div className="grid grid-cols-[1.4fr_110px_140px_110px_110px_110px_90px] gap-2 px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600">
                <span>Client</span>
                <span className="text-right">Active employees</span>
                <span className="text-right">Payroll (30d)</span>
                <span className="text-right">Pending leave</span>
                <span className="text-right">Pending TS</span>
                <span className="text-right">Pay changes</span>
                <span className="text-right">Actions</span>
              </div>
              {clients.map((c) => (
                <div key={c.client_id}
                  className="grid grid-cols-[1.4fr_110px_140px_110px_110px_110px_90px] gap-2 px-3 py-2 border-b border-zinc-100 last:border-b-0 text-sm items-center hover:bg-zinc-50"
                  data-testid={`keypay-rollup-client-${c.client_id}`}>
                  <span className="truncate">
                    <div className="font-semibold">{c.client_name}</div>
                    <div className="text-[10px] text-zinc-500 font-mono">
                      biz {c.business_id} · {c.region} · synced {fmtRel(c.synced_at)}
                      {(c.errors || []).length > 0 && (
                        <span className="ml-2 text-red-600">· {c.errors.length} error(s)</span>
                      )}
                    </div>
                  </span>
                  <span className="font-mono text-right text-emerald-700">
                    {c.active_employee_count ?? 0}
                  </span>
                  <span className="font-mono text-right text-emerald-700">
                    {fmtMoney(c.last_30d_spend)}
                  </span>
                  <span className={`font-mono text-right ${
                    (c.pending_leave_count ?? 0) > 0 ? "text-amber-700 font-semibold" : "text-zinc-400"
                  }`}>
                    {c.pending_leave_count ?? 0}
                  </span>
                  <span className={`font-mono text-right ${
                    (c.pending_timesheet_count ?? 0) > 0 ? "text-amber-700 font-semibold" : "text-zinc-400"
                  }`}>
                    {c.pending_timesheet_count ?? 0}
                  </span>
                  <span className={`font-mono text-right ${
                    (c.pay_changes_detected ?? 0) > 0 ? "text-purple-700 font-semibold" : "text-zinc-400"
                  }`}>
                    {c.pay_changes_detected ?? 0}
                  </span>
                  <span className="text-right">
                    <Link to={`/integrations/keypay/clients/${c.client_id}`}
                      data-testid={`keypay-rollup-open-${c.client_id}`}>
                      <Badge variant="outline"
                        className="rounded-sm text-[9px] cursor-pointer hover:bg-[#FF6B35]/10 hover:border-[#FF6B35]">
                        Open →
                      </Badge>
                    </Link>
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function KpiTile({ label, value, accent, icon: Icon }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-3 bg-white">
      <div className="label-overline text-[9px] flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <div className={`font-display font-bold text-xl mt-1 ${accent || ""}`}>
        {value}
      </div>
    </div>
  );
}

function fmtMoney(n) {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function fmtRel(iso) {
  if (!iso) return "never";
  const d = new Date(iso);
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
